import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Download,
  FileText,
  ArrowRight,
  ShieldCheck,
  Share2,
  Bookmark,
  Check,
} from "lucide-react";

import Disclaimer from "@/components/Disclaimer";
import CallbackDialog from "@/components/CallbackDialog";
import LockedPreview from "@/components/wizard/LockedPreview";
import SuccessReceipt from "@/components/wizard/SuccessReceipt";
import EmailResumeLinkDialog from "@/components/wizard/EmailResumeLinkDialog";
import { generateWill, API, saveDraft, fetchDraft } from "@/lib/api";

import PersonForm from "@/components/wizard/PersonForm";
import FamilyStep from "@/components/wizard/FamilyStep";
import AssetsStep from "@/components/wizard/AssetsStep";
import DistributionStep from "@/components/wizard/DistributionStep";
import ExecutorStep from "@/components/wizard/ExecutorStep";
import GuardianStep from "@/components/wizard/GuardianStep";
import InstructionsStep from "@/components/wizard/InstructionsStep";
import ClausesStep from "@/components/wizard/ClausesStep";
import ReviewStep from "@/components/wizard/ReviewStep";
import PaymentGate from "@/components/wizard/PaymentGate";
import PostPaymentAddons from "@/components/wizard/PostPaymentAddons";
import {
  validatePerson,
  validateFamily,
  validateAssets,
  validateDistribution,
  validateExecutor,
  validateGuardian,
  buildChecklist,
  capitalizeName,
  completionPercent,
  hasMinors,
  uid,
} from "@/components/wizard/validation";

const emptyPerson = () => ({
  full_name: "",
  date_of_birth: "",
  address: "",
  city: "",
  religion: "",
  marital_status: "",
  sound_mind: false,
});

const emptyExecutor = () => ({
  name: "",
  relationship: "",
  phone: "",
  email: "",
  backup_name: "",
  backup_relationship: "",
  backup_phone: "",
  backup_email: "",
});

const emptyGuardian = () => ({
  name: "",
  relationship: "",
  backup_name: "",
  backup_relationship: "",
});

export default function WillWizard({ mode = "single" }) {
  const isJoint = mode === "joint";

  // ---------- state ----------
  const [step, setStep] = useState(1);
  const [person, setPerson] = useState(emptyPerson());
  const [person2, setPerson2] = useState(emptyPerson());
  const [family, setFamily] = useState([
    { id: uid(), name: "", relationship: "Spouse", age: "" },
  ]);
  const [assets, setAssets] = useState([
    { id: uid(), type: "Property", description: "", value: "" },
  ]);
  const [distribution, setDistribution] = useState({
    mode: "",
    percentages: [],
    asset_assignments: [],
  });
  const [executor, setExecutor] = useState(emptyExecutor());
  const [guardian, setGuardian] = useState(emptyGuardian());
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [digitalAssets, setDigitalAssets] = useState([]);
  const [funeralWishes, setFuneralWishes] = useState({ kind: "", custom: "" });
  const [clauseToggles, setClauseToggles] = useState({
    debts: true,
    residuary: true,
    executor_powers: true,
    digital_assets: true,
    funeral_wishes: true,
  });

  const [loading, setLoading] = useState(false);
  const [willText, setWillText] = useState("");
  const [willMeta, setWillMeta] = useState(null); // {id, pdf_url, docx_url}
  const [paid, setPaid] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [addonDialogOpen, setAddonDialogOpen] = useState(false);

  // ---------- Draft resume ----------
  // Lightweight: persist wizard state to backend after each change, keyed by
  // a UUID surfaced in the URL as `?d=<id>`. Users bookmark the URL to resume.
  const [draftId, setDraftId] = useState(null);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftCopied, setDraftCopied] = useState(false);

  // hydrate on mount if ?d=<id> present
  useEffect(() => {
    const url = new URL(window.location.href);
    const id = url.searchParams.get("d");
    if (!id) {
      setDraftHydrated(true);
      return;
    }
    fetchDraft(id)
      .then((doc) => {
        const s = doc?.state || {};
        if (s.step) setStep(s.step);
        if (s.person) setPerson(s.person);
        if (s.person2) setPerson2(s.person2);
        if (s.family) setFamily(s.family);
        if (s.assets) setAssets(s.assets);
        if (s.distribution) setDistribution(s.distribution);
        if (s.executor) setExecutor(s.executor);
        if (s.guardian) setGuardian(s.guardian);
        if (typeof s.specialInstructions === "string")
          setSpecialInstructions(s.specialInstructions);
        if (s.digitalAssets) setDigitalAssets(s.digitalAssets);
        if (s.funeralWishes) setFuneralWishes(s.funeralWishes);
        if (s.clauseToggles) setClauseToggles(s.clauseToggles);
        setDraftId(id);
      })
      .catch(() => toast.message("Couldn't load draft. Starting fresh."))
      .finally(() => setDraftHydrated(true));
  }, []);

  // debounced auto-save
  useEffect(() => {
    if (!draftHydrated) return;
    if (willText) return; // don't keep saving after generation
    const t = setTimeout(() => {
      const state = {
        step, person, person2, family, assets, distribution, executor,
        guardian, specialInstructions, digitalAssets, funeralWishes, clauseToggles,
      };
      saveDraft(draftId, state)
        .then((res) => {
          if (!draftId && res?.id) {
            setDraftId(res.id);
            const url = new URL(window.location.href);
            url.searchParams.set("d", res.id);
            window.history.replaceState({}, "", url.toString());
          }
        })
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [
    draftHydrated, willText, step, person, person2, family, assets,
    distribution, executor, guardian, specialInstructions, digitalAssets,
    funeralWishes, clauseToggles, draftId,
  ]);

  const copyResumeLink = () => {
    if (!draftId) return;
    const url = `${window.location.origin}/will-generator?d=${draftId}`;
    navigator.clipboard?.writeText(url).then(() => {
      setDraftCopied(true);
      toast.success("Resume link copied. Bookmark it to continue later.");
      setTimeout(() => setDraftCopied(false), 2500);
    });
  };

  const minorsExist = useMemo(() => hasMinors(family), [family]);

  // Dynamic step sequence — Guardian step only shown when minors exist
  const steps = useMemo(() => {
    const base = [
      { id: "personal", label: "Personal Details" },
      { id: "family", label: "Family Members" },
      { id: "assets", label: "Assets" },
      { id: "distribution", label: "Distribution" },
      { id: "executor", label: "Executor" },
    ];
    if (minorsExist) base.push({ id: "guardian", label: "Guardian" });
    base.push({ id: "instructions", label: "Final Wishes" });
    base.push({ id: "clauses", label: "Legal Clauses" });
    base.push({ id: "review", label: "Review & Generate" });
    return base;
  }, [minorsExist]);

  // Keep step in bounds when steps array changes
  const currentStepId = steps[Math.min(step - 1, steps.length - 1)]?.id;

  const checklist = useMemo(
    () =>
      buildChecklist({
        mode,
        person,
        person2,
        family,
        assets,
        distribution,
        executor,
        guardian,
        minorsExist,
      }),
    [mode, person, person2, family, assets, distribution, executor, guardian, minorsExist]
  );
  const percent = completionPercent(checklist);

  // ---------- validation ----------
  const validateStepById = (id) => {
    if (id === "personal") {
      const e1 = validatePerson(person, isJoint ? "Testator 1" : "You");
      if (e1) return e1;
      if (isJoint) {
        const e2 = validatePerson(person2, "Testator 2");
        if (e2) return e2;
      }
    }
    if (id === "family") return validateFamily(family);
    if (id === "assets") return validateAssets(assets);
    if (id === "distribution") return validateDistribution(distribution, assets);
    if (id === "executor") return validateExecutor(executor);
    if (id === "guardian") return validateGuardian(guardian, minorsExist);
    // instructions: no validation (optional)
    return null;
  };

  const goNext = () => {
    const id = steps[step - 1].id;
    const err = validateStepById(id);
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(steps.length, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const jumpTo = (stepNumber) => {
    if (stepNumber >= 1 && stepNumber <= steps.length) {
      setStep(stepNumber);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ---------- submit ----------
  const submit = async () => {
    for (let i = 0; i < steps.length - 1; i++) {
      const err = validateStepById(steps[i].id);
      if (err) {
        setStep(i + 1);
        toast.error(err);
        return;
      }
    }

    setLoading(true);
    try {
      // Light Validation — trim spaces & auto-capitalise names just before sending
      // so the generated legal document looks clean.
      const cleanPerson = (p) => ({
        ...p,
        full_name: capitalizeName(p.full_name || ""),
        address: (p.address || "").replace(/\s+/g, " ").trim(),
        city: (p.city || "").replace(/\s+/g, " ").trim(),
      });
      const cleanedPerson = cleanPerson(person);
      const cleanedPerson2 = isJoint ? cleanPerson(person2) : null;

      const basePayload = {
        family: family.map((f) => ({
          ...f,
          name: capitalizeName(f.name || ""),
          age: Number(f.age),
          is_minor: Number(f.age) < 18,
        })),
        assets: assets.map((a) => ({
          ...a,
          description: (a.description || "").replace(/\s+/g, " ").trim(),
        })),
        distribution: {
          mode: distribution.mode,
          percentages: distribution.percentages.map((p) => ({
            beneficiary_name: capitalizeName(p.beneficiary_name || ""),
            beneficiary_relationship: (p.beneficiary_relationship || "").trim(),
            share: Number(p.share),
            is_minor: !!p.is_minor,
            guardian_name: capitalizeName(p.guardian_name || ""),
            guardian_relation: (p.guardian_relation || "").trim(),
          })),
          asset_assignments: distribution.asset_assignments.map((a) => ({
            asset_id: a.asset_id,
            asset_description: (a.asset_description || "").replace(/\s+/g, " ").trim(),
            beneficiaries: (a.beneficiaries || []).map((b) => capitalizeName(b || "")),
            notes: (a.notes || "").trim(),
          })),
        },
        executor: {
          ...executor,
          name: capitalizeName(executor.name || ""),
          relationship: (executor.relationship || "").trim(),
          phone: (executor.phone || "").trim(),
          email: (executor.email || "").trim(),
          backup_name: capitalizeName(executor.backup_name || ""),
          backup_relationship: (executor.backup_relationship || "").trim(),
          backup_phone: (executor.backup_phone || "").trim(),
          backup_email: (executor.backup_email || "").trim(),
        },
        guardian: minorsExist
          ? {
              ...guardian,
              name: capitalizeName(guardian.name || ""),
              relationship: (guardian.relationship || "").trim(),
              backup_name: capitalizeName(guardian.backup_name || ""),
              backup_relationship: (guardian.backup_relationship || "").trim(),
            }
          : null,
        special_instructions: (specialInstructions || "").trim(),
        digital_assets: digitalAssets,
        funeral_wishes:
          funeralWishes.kind || funeralWishes.custom ? funeralWishes : null,
        clause_toggles: clauseToggles,
      };
      const payload = isJoint
        ? { person1: cleanedPerson, person2: cleanedPerson2, ...basePayload }
        : { person: cleanedPerson, ...basePayload };

      const res = await generateWill(payload);
      setWillText(res.will_text);
      setWillMeta({
        id: res.id,
        pdf_url: res.pdf_url,
        docx_url: res.docx_url,
        created_at: res.created_at,
        plain_summary: res.plain_summary || "",
        lawyer_review_flags: res.lawyer_review_flags || [],
      });
      toast.success("Your will has been generated.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(detail ? `Will generation failed: ${detail}` : "Will generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [downloading, setDownloading] = useState(null); // "pdf" | "docx" | null

  const shareOnWhatsApp = () => {
    if (!willMeta?.pdf_url) return;
    const origin =
      (typeof window !== "undefined" && window.location?.origin) || "";
    const fullUrl = `${origin}${willMeta.pdf_url}`;
    const testator = isJoint
      ? `${person?.full_name || ""}${person2?.full_name ? " & " + person2.full_name : ""}`.trim()
      : (person?.full_name || "").trim();
    const intro = isJoint
      ? "We have drafted our Last Will and Testament on WillSeva."
      : "I have drafted my Last Will and Testament on WillSeva.";
    const lines = [
      `Hello,`,
      ``,
      `${intro} It has been prepared using a guided, India-Succession-Act aligned legal will builder.`,
      ``,
      `You can download the PDF here:`,
      fullUrl,
      ``,
      testator ? `— ${testator}` : "",
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));
    const url = `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener");
  };

  const downloadFile = async (fmt) => {
    if (!willMeta?.id) {
      toast.error("Will not yet generated.");
      return;
    }
    setDownloading(fmt);
    const url = `${API}/wills/${willMeta.id}/download/${fmt}`;

    // Robust download with one retry — protects against transient network blips
    // and the brief window where the backend self-heals a missing file.
    const tryFetch = async () => {
      const res = await fetch(url, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
      });
      if (!res.ok) {
        let detail = "";
        try {
          const body = await res.clone().json();
          detail = body?.detail || "";
        } catch (_) {
          try {
            detail = (await res.clone().text()).slice(0, 200);
          } catch (_) {}
        }
        const err = new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ""}`);
        err.status = res.status;
        throw err;
      }
      return res;
    };

    try {
      let res;
      try {
        res = await tryFetch();
      } catch (firstErr) {
        // Retry once after a short pause for 5xx / network errors.
        // 4xx errors are deterministic — don't retry.
        if (!firstErr?.status || firstErr.status >= 500) {
          await new Promise((r) => setTimeout(r, 800));
          res = await tryFetch();
        } else {
          throw firstErr;
        }
      }

      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        throw new Error("Empty file received from server.");
      }

      // Honour server-provided filename from Content-Disposition
      let filename = `WillSeva-Will.${fmt}`;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      if (m && m[1]) filename = decodeURIComponent(m[1]);

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1500);
    } catch (err) {
      console.error("download failed:", err);
      // Surface the real error so the user / our support can act on it.
      const detail = err?.message || "Unknown error";
      toast.error(`${fmt.toUpperCase()} download failed: ${detail}`);
      // Final fallback — open the URL in a new tab so the user can still save it.
      try {
        window.open(url, "_blank", "noopener");
      } catch (_) {
        toast.error("Please try again or contact us on WhatsApp for support.");
      }
    } finally {
      setDownloading(null);
    }
  };

  // ---------- output view ----------
  if (willText) {
    const testatorName = (person?.full_name || "").trim();
    if (!paid) {
      return (
        <div data-testid="will-output-view">
          <div className="rounded-md border border-gold/40 bg-[#FBF7EE] p-5 text-sm text-navy">
            <div className="font-serif text-lg">
              ✨ Your Will draft is ready.
            </div>
            <p className="mt-1 text-[#4A5568]">
              We've assembled your professionally structured Last Will and Testament.
              Complete a secure payment to unlock the full document and download as PDF or DOCX.
            </p>
            <div className="mt-3 text-xs uppercase tracking-[0.18em] text-[#4A5568]">
              Reference: <span className="font-mono text-navy">{willMeta?.id}</span>
            </div>
          </div>

          <div className="mt-6">
            <LockedPreview willId={willMeta?.id} />
          </div>

          <div className="mt-6">
            <PaymentGate
              willId={willMeta?.id}
              testator={testatorName}
              userEmail={executor?.email || ""}
              userPhone={executor?.phone || ""}
              onPaid={(info) => {
                setPaid(true);
                setPaymentInfo(info);
                setAddonDialogOpen(true);
              }}
            />
          </div>

          <div className="mt-6">
            <Disclaimer testId="will-output-disclaimer-pre-payment" compact />
          </div>
        </div>
      );
    }

    return (
      <div data-testid="will-output-view-paid">
        <PostPaymentAddons
          open={addonDialogOpen}
          willId={willMeta?.id}
          onClose={() => setAddonDialogOpen(false)}
        />
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 p-5 text-sm"
          data-testid="payment-success-banner"
        >
          <div className="font-serif text-lg text-emerald-900">
            ✓ Payment successful — Your Will is Ready.
          </div>
          <p className="mt-1 text-emerald-800">
            Thank you. Your professionally structured will is now unlocked. Download below
            and sign in the presence of two adult witnesses who are not beneficiaries.
          </p>
          <p className="mt-2 text-xs text-emerald-900/80" data-testid="witness-soft-reminder">
            For legal validity, your Will should be signed in the presence of two witnesses.
          </p>
          {paymentInfo?.payment_id && (
            <div className="mt-3 grid gap-1 text-xs text-emerald-900/80 sm:grid-cols-2">
              <div>
                Payment ID:{" "}
                <span className="font-mono" data-testid="payment-success-payment-id">
                  {paymentInfo.payment_id}
                </span>
              </div>
              <div>
                Will Reference:{" "}
                <span className="font-mono">{willMeta?.id}</span>
              </div>
            </div>
          )}
        </div>

        <SuccessReceipt
          willId={willMeta?.id}
          paymentId={paymentInfo?.payment_id}
          createdAt={willMeta?.created_at}
        />

        <div
          className="mt-4 flex items-start gap-3 rounded-md border border-red-200 bg-red-50/70 p-5 text-sm text-[#0A1128]"
          data-testid="witness-guidance-callout"
        >
          <ShieldCheck size={20} className="mt-0.5 shrink-0 text-red-600" />
          <div>
            <div className="font-semibold text-red-700">
              Witness rules — must be followed for your will to be valid
            </div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[#4A5568]">
              <li>Choose <span className="font-medium text-navy">two adult witnesses</span> (18+ years, of sound mind).</li>
              <li><span className="font-medium text-navy">Witnesses must NOT be beneficiaries</span> under this will, nor the spouse of any beneficiary.</li>
              <li>You and both witnesses must sign in each other's presence on the same day.</li>
              <li>Each witness should also write their full name, address and signature.</li>
            </ul>
          </div>
        </div>

        <Card className="mt-6 border-[#0A1128]/10 shadow-sm">
          <CardContent className="p-6 sm:p-10">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#0A1128]/10 pb-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                  Generated Document
                </div>
                <div className="mt-1 font-serif text-xl text-navy">
                  Last Will and Testament
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => downloadFile("pdf")}
                  disabled={downloading === "pdf"}
                  className="bg-gold text-navy hover:bg-[#E5C87A]"
                  data-testid="download-pdf-btn"
                >
                  <Download size={16} className="mr-2" />
                  {downloading === "pdf" ? "Preparing…" : "Download PDF"}
                </Button>
                <Button
                  onClick={() => downloadFile("docx")}
                  disabled={downloading === "docx"}
                  variant="outline"
                  className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
                  data-testid="download-docx-btn"
                >
                  <FileText size={16} className="mr-2" />
                  {downloading === "docx" ? "Preparing…" : "Download DOCX"}
                </Button>
                <Button
                  onClick={shareOnWhatsApp}
                  variant="outline"
                  className="border-[#25D366] text-[#0A1128] hover:bg-[#25D366]/10"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 size={16} className="mr-2 text-[#25D366]" />
                  Share on WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
                  onClick={() => {
                    setWillText("");
                    setWillMeta(null);
                    setPaid(false);
                    setPaymentInfo(null);
                    setStep(1);
                  }}
                  data-testid="start-over-btn"
                >
                  Start Over
                </Button>
              </div>
            </div>

            <div
              className="will-doc mt-6 max-h-[640px] overflow-y-auto rounded-md border border-[#0A1128]/10 bg-white p-6 text-sm sm:text-[15px]"
              data-testid="will-text-output"
            >
              {willText}
            </div>
          </CardContent>
        </Card>

        {/* Witness & executor guide */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="signing-guide">
          <Card className="border-[#0A1128]/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
                  <ShieldCheck size={18} />
                </div>
                <div className="font-serif text-lg text-navy">Your next steps</div>
              </div>
              <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-[#4A5568]">
                <li>Print your Will on plain white paper.</li>
                <li>Sign in the presence of <span className="font-medium text-navy">two adult witnesses</span> who are not beneficiaries (and not the spouse of any beneficiary).</li>
                <li>Both witnesses must sign in your presence.</li>
                <li>Store the original safely (bank locker / fireproof safe) and share a copy with your executor.</li>
                <li>Registration is optional, but may provide additional protection — visit your sub-registrar's office.</li>
              </ol>
            </CardContent>
          </Card>
          <Card className="border-[#0A1128]/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
                  <ShieldCheck size={18} />
                </div>
                <div className="font-serif text-lg text-navy">Executor instructions</div>
              </div>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-[#4A5568]">
                <li>Inform your executor where the signed will is stored.</li>
                <li>Keep the original in a bank locker or fireproof safe.</li>
                <li>Share one scanned copy with a trusted family member.</li>
                <li>Review the will every 3 years or after major life events (marriage, childbirth, property purchase).</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 rounded-md border border-gold/40 bg-white p-6 sm:p-8" data-testid="lawyer-review-prompt">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                Want extra assurance?
              </div>
              <h3 className="mt-2 font-serif text-xl text-navy sm:text-2xl">
                Have Senior Lawyers review this will.
              </h3>
              <p className="mt-1 text-sm text-[#4A5568]">
                Our legal consultants are senior advocates specialising exclusively in succession, testamentary and estate planning law — ensuring every Will meets the highest legal standard.
              </p>
            </div>
            <Link to="/will-generator" className="hidden">
              {/* keep link import used */}
            </Link>
            <CallbackDialog
              label="Request a Callback"
              plan="Lawyer Review"
              className="bg-navy text-white hover:bg-[#1A2642]"
              testId="paid-lawyer-review-callback"
              buttonTestId="request-review-cta"
            />
          </div>
        </div>

        <div className="mt-6">
          <Disclaimer testId="will-output-disclaimer" />
        </div>
      </div>
    );
  }

  // ---------- wizard view ----------
  const totalSteps = steps.length;
  const barPct = (step / totalSteps) * 100;
  const currentIdx = step - 1;

  return (
    <div data-testid={`wizard-${mode}`}>
      {/* Sticky progress */}
      <div className="sticky top-16 z-20 -mx-5 mb-8 border-b border-[#0A1128]/10 bg-white/95 px-5 py-4 backdrop-blur sm:-mx-8 sm:px-8">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              Step {step} of {totalSteps}
            </div>
            <div className="mt-1 font-serif text-lg text-navy sm:text-xl">
              {steps[currentIdx].label}
            </div>
          </div>
          <div
            className="flex flex-col items-end"
            data-testid="wizard-completion-badge"
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4A5568]">
              Will Completion
            </div>
            <div className="mt-0.5 font-serif text-lg text-navy">{percent}%</div>
          </div>
        </div>
        <Progress
          value={barPct}
          className="mt-3 h-1.5 bg-[#0A1128]/10 [&>div]:bg-[#C5A059]"
          data-testid="wizard-progress"
        />
        {/* Step dots */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {steps.map((s, i) => (
            <button
              type="button"
              key={s.id}
              onClick={() => i < step - 1 && jumpTo(i + 1)}
              disabled={i >= step}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i < step - 1
                  ? "bg-[#C5A059] cursor-pointer"
                  : i === step - 1
                  ? "bg-navy"
                  : "bg-[#0A1128]/10"
              }`}
              aria-label={`Go to step ${i + 1}: ${s.label}`}
              data-testid={`wizard-step-dot-${i}`}
            />
          ))}
        </div>
      </div>

      {/* Resume link banner (visible only once draft is saved and not yet generated) */}
      {draftId && !willText && (
        <div
          className="mb-4 flex flex-col gap-2 rounded-md border border-gold/40 bg-[#FBF7EE] px-4 py-3 text-xs text-navy sm:flex-row sm:items-center sm:justify-between"
          data-testid="resume-link-banner"
        >
          <div className="flex items-start gap-2">
            <Bookmark size={14} className="mt-0.5 shrink-0 text-gold" />
            <span>
              Your progress is auto-saved. Bookmark this page or copy the
              resume link to continue later from any device.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={copyResumeLink}
              className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
              data-testid="resume-copy-link-btn"
            >
              {draftCopied ? (
                <><Check size={14} className="mr-1.5" /> Copied</>
              ) : (
                <><Bookmark size={14} className="mr-1.5" /> Copy Resume Link</>
              )}
            </Button>
            <EmailResumeLinkDialog
              draftId={draftId}
              resumeUrl={`${typeof window !== "undefined" ? window.location.origin : ""}/will-generator?d=${draftId || ""}`}
            />
          </div>
        </div>
      )}

      <Card className="border-[#0A1128]/10 shadow-sm">
        <CardContent className="p-6 sm:p-10">
          {currentStepId === "personal" && (
            <div className="space-y-8" data-testid="step-personal-info">
              <PersonForm
                person={person}
                setPerson={setPerson}
                label={isJoint ? "Testator 1 — Personal Details" : "Personal Details"}
                idPrefix="p1"
              />
              {isJoint && (
                <div className="border-t border-[#0A1128]/10 pt-8">
                  <PersonForm
                    person={person2}
                    setPerson={setPerson2}
                    label="Testator 2 — Personal Details"
                    idPrefix="p2"
                  />
                </div>
              )}
            </div>
          )}

          {currentStepId === "family" && (
            <FamilyStep family={family} setFamily={setFamily} />
          )}

          {currentStepId === "assets" && (
            <AssetsStep assets={assets} setAssets={setAssets} />
          )}

          {currentStepId === "distribution" && (
            <DistributionStep
              distribution={distribution}
              setDistribution={setDistribution}
              family={family}
              assets={assets}
            />
          )}

          {currentStepId === "executor" && (
            <ExecutorStep
              executor={executor}
              setExecutor={setExecutor}
              distribution={distribution}
            />
          )}

          {currentStepId === "guardian" && (
            <GuardianStep
              guardian={guardian}
              setGuardian={setGuardian}
              family={family}
            />
          )}

          {currentStepId === "instructions" && (
            <InstructionsStep
              specialInstructions={specialInstructions}
              setSpecialInstructions={setSpecialInstructions}
              digitalAssets={digitalAssets}
              setDigitalAssets={setDigitalAssets}
              funeralWishes={funeralWishes}
              setFuneralWishes={setFuneralWishes}
            />
          )}

          {currentStepId === "clauses" && (
            <ClausesStep
              clauseToggles={clauseToggles}
              setClauseToggles={setClauseToggles}
            />
          )}

          {currentStepId === "review" && (
            <ReviewStep
              mode={mode}
              person={person}
              person2={person2}
              family={family}
              assets={assets}
              distribution={distribution}
              executor={executor}
              guardian={minorsExist ? guardian : null}
              specialInstructions={specialInstructions}
              checks={checklist}
              percent={percent}
              onJump={jumpTo}
            />
          )}

          {/* Nav */}
          <div className="mt-10 flex items-center justify-between border-t border-[#0A1128]/10 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={step === 1 || loading}
              className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
              data-testid="wizard-prev-btn"
            >
              <ChevronLeft size={16} className="mr-1" /> Back
            </Button>
            {step < totalSteps ? (
              <Button
                type="button"
                onClick={goNext}
                className="bg-navy text-white hover:bg-[#1A2642]"
                data-testid="wizard-next-btn"
              >
                Next <ChevronRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={submit}
                disabled={loading || percent < 100}
                size="lg"
                className="bg-gold text-navy hover:bg-[#E5C87A] disabled:cursor-not-allowed disabled:bg-gold/50"
                data-testid="wizard-generate-btn"
              >
                {loading ? (
                  "Generating…"
                ) : (
                  <>
                    <Sparkles size={16} className="mr-2" />
                    Generate My Will
                  </>
                )}
              </Button>
            )}
          </div>

          {step === totalSteps && percent < 100 && (
            <p className="mt-4 text-xs text-red-600" data-testid="wizard-locked-hint">
              Generation is locked until all validation checks pass. Fix the red items above.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="mt-8">
        <Disclaimer testId="wizard-disclaimer" compact />
      </div>
    </div>
  );
}
